/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useState, useCallback } from 'react';

import { Colors, Spacing } from 'src/common/ui/base';
import { Button, FormControl, Input, InputLabel } from 'src/common/ui/components';
import {
  formControlStyles,
  inputStyles,
  errorStyles,
} from 'src/components/pages/providers/Provider.Form.styles';
import useRootStore from 'src/stores/useRootStore';

export type FileValue = {
  id: string | null;
  filename: string | null;
  url: string | null;
} | null;

export const FileUpload = ({
  label,
  name,
  value,
  onChange,
  required = false,
  error,
}: {
  label: string;
  name: string;
  value: FileValue;
  onChange: (value: FileValue) => void;
  required?: boolean;
  error?: string;
}) => {
  const [touched, setTouched] = useState(false);
  const touchedAndError = touched && !!error;

  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const rootStore = useRootStore();

  const handleSelectFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        setUploading(true);
        const upload: FileValue = await rootStore.uploadFile(
          event.target.files[0],
          (progress: { loaded: number; total: number }) => {
            setUploadProgress(progress.loaded / progress.total);
          },
        );
        onChange(upload);
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [rootStore, onChange],
  );

  const handleClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  return (
    <FormControl css={formControlStyles} required={required} error={touchedAndError}>
      <InputLabel htmlFor={name}>{label}</InputLabel>
      <div
        css={css`
          position: relative;
        `}
      >
        {uploading && (
          <div
            css={css`
              position: absolute;
              background-color: ${Colors.BlueSpruce};
              height: 100%;
              top: 0;
              left: 0;
              opacity: 0.3;
              width: ${uploadProgress * 100}%;
            `}
          />
        )}
        <Input
          css={inputStyles}
          endAdornment={
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              {value?.filename && (
                <Button
                  css={css`
                    position: relative;
                    z-index: 2;
                  `}
                  onClick={handleClear}
                  disabled={uploading}
                >
                  Clear
                </Button>
              )}
              <input
                id={name}
                name={name}
                type="file"
                accept="image/*"
                onChange={handleSelectFile}
                css={css`
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: ${Spacing.Unit4}px;
                  cursor: pointer;
                  opacity: 0;
                `}
              />
            </div>
          }
          placeholder={value?.filename ? '' : 'Select file...'}
          id={`${name}-filename`}
          name={`${name}-filename`}
          onBlur={() => !touched && setTouched(true)}
          readOnly
          type="text"
          value={value?.filename || ''}
        />
      </div>
      {touchedAndError && <div css={errorStyles}>{error}</div>}
      {value?.url && (
        <img
          src={value.url}
          alt="Avatar"
          css={css`
            width: 100px;
            height: 100px;
            border-radius: 50%;
            margin-top: ${Spacing.Unit2}px;
            object-fit: cover;
          `}
        />
      )}
    </FormControl>
  );
};
